// apps/web/src/admin/AdminApp.tsx
import { useState } from 'react';
import { TopBar } from '../TopBar.js';
import { useTheme } from '../lib/theme.js';
import { SunIcon, MoonIcon } from '../icons.js';
import { useAppStore } from '../store/useAppStore.js';
import { useAdminStatus } from './useAdminStatus.js';
import { useScrollToTop } from '../hooks/useScrollToTop.js';
import { AdminLiveOpsView } from './AdminLiveOpsView.js';
import { AdminRevenueView } from './AdminRevenueView.js';
import { AdminUsersView } from './AdminUsersView.js';
import { AdminCallActivityView } from './AdminCallActivityView.js';
import { AdminGuidesView } from './AdminGuidesView.js';
import { AdminLoginsView } from './AdminLoginsView.js';
import { AdminSystemHealthView } from './AdminSystemHealthView.js';
import { AdminAuditView } from './AdminAuditView.js';
import { AdminApplicationsView } from './AdminApplicationsView.js';
import { AdminIntegrityView } from './AdminIntegrityView.js';

type Panel = 'applications' | 'live' | 'revenue' | 'users' | 'calls' | 'guides' | 'logins' | 'system' | 'audit' | 'integrity';

const NAV: { key: Panel; label: string }[] = [
  { key: 'applications', label: 'Applications' },
  { key: 'live',    label: 'Live Ops'  },
  { key: 'revenue', label: 'Revenue'   },
  { key: 'users',   label: 'Users'     },
  { key: 'calls',   label: 'Calls'     },
  { key: 'guides',  label: 'Guides'    },
  { key: 'logins',  label: 'Logins'    },
  { key: 'system',  label: 'System'    },
  { key: 'integrity', label: 'Integrity' },
  { key: 'audit',   label: 'Admin Audit log' },
];

const TITLES: Record<Panel, string> = {
  applications: 'Guide applications',
  live:    'Live operations',
  revenue: 'Revenue & minutes',
  users:   'User management',
  calls:   'Call activity & quality',
  guides:  'Guide earnings',
  logins:  'Login events',
  system:  'System health',
  integrity: 'Ledger integrity',
  audit:   'Admin Audit log',
};

export function AdminApp() {
  const status = useAdminStatus();
  const [panel, setPanel] = useState<Panel>('live');
  const { theme, toggle: toggleTheme } = useTheme();
  const signOut = useAppStore((s) => s.signOut);
  useScrollToTop(panel);

  if (status === 'loading') {
    return <div className="min-h-screen flex items-center justify-center text-ink-500">Loading…</div>;
  }
  if (status === 'unauthenticated') {
    const here = encodeURIComponent(window.location.pathname);
    window.location.replace(`/?next=${here}`);
    return null;
  }
  if (status === 'not-admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2">Page not found</h1>
          <p className="text-ink-500">The page you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-canvas-light dark:bg-canvas-dark text-ink-900 dark:text-ink-100">
      <TopBar right={<>
        <span className="text-xs px-2.5 py-1 rounded-full bg-white/[0.06] ring-1 ring-white/10 text-ink-100">Admin</span>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="p-2 rounded-lg text-ink-300 hover:text-white hover:bg-white/10 transition"
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
        <a href="/" className="text-[0.85rem] px-3 py-1.5 rounded-md bg-white/[0.06] ring-1 ring-white/10 text-ink-100 hover:bg-white/10 transition">← Back to app</a>
        <button
          type="button"
          onClick={() => { void signOut().then(() => { window.location.href = '/'; }); }}
          className="text-[0.85rem] text-ink-300 hover:text-white px-3 py-1.5 rounded-md hover:bg-white/10 transition"
        >
          Sign out
        </button>
      </>} />
      <div className="flex flex-1 min-h-0">
      <nav className="w-48 border-r p-4 space-y-1 text-sm">
        <div className="font-semibold text-base mb-3">ASL Go Admin</div>
        {NAV.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setPanel(key)}
            className={`block w-full text-left px-2 py-1 rounded ${panel === key ? 'bg-ink-100 dark:bg-white/5' : ''}`}
          >
            {label}
          </button>
        ))}
      </nav>
      <main className="flex-1 p-6">
        <header className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold">{TITLES[panel]}</h1>
          <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">viewing as admin</span>
        </header>
        {panel === 'applications' ? <AdminApplicationsView /> : null}
        {panel === 'live'    ? <AdminLiveOpsView />      : null}
        {panel === 'revenue' ? <AdminRevenueView />      : null}
        {panel === 'users'   ? <AdminUsersView />        : null}
        {panel === 'calls'   ? <AdminCallActivityView /> : null}
        {panel === 'guides'  ? <AdminGuidesView />       : null}
        {panel === 'logins'  ? <AdminLoginsView />       : null}
        {panel === 'system'  ? <AdminSystemHealthView /> : null}
        {panel === 'integrity' ? <AdminIntegrityView /> : null}
        {panel === 'audit'   ? <AdminAuditView />        : null}
      </main>
      </div>
    </div>
  );
}
